import clsx, { ClassValue } from 'clsx';
import { format, isThisYear, isToday, parseISO } from 'date-fns';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getInitials(name?: string): string {
  if (!name) return '';

  const words = name.trim().split(' ').filter(Boolean);

  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }

  return words[0][0].toUpperCase();
}

export function formatChatDate(iso: string): string {
  if (!iso) return '';

  const date = parseISO(iso);

  if (isToday(date)) {
    return format(date, 'HH:mm');
  }

  if (isThisYear(date)) {
    return format(date, 'dd.MM HH:mm');
  }

  return format(date, 'dd.MM.yy HH:mm');
}

export function trimWithEllipsis(text: string): string {
  const limit = 42;
  const ellipsis = '…';

  return text.length <= limit ? text : text.slice(0, limit - ellipsis.length) + ellipsis;
}

export function formatBytes(bytes: number): string {
  const BYTES_IN_KB = 1024;
  const BYTES_IN_MB = BYTES_IN_KB * 1024;

  if (bytes >= BYTES_IN_MB) {
    const mb = bytes / BYTES_IN_MB;

    return mb.toFixed(2) + ' MB';
  } else {
    const kb = bytes / BYTES_IN_KB;

    return kb.toFixed(1) + ' KB';
  }
}

export const readFileContent = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export type EncodedFile = {
  name: string;
  size: number;
  type: string;
  base64: string;
};

/** Read file as base64 */
export const fileToBase64 = (file: File): Promise<EncodedFile> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    // Use DataURL then strip prefix to avoid binary corruption
    reader.onload = () => {
      const base64 = String(reader.result || '');
      resolve({
        name: file.name,
        size: file.size,
        type: file.type,
        base64,
      });
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
